import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import PropTypes from "prop-types";
import { Flag, Loader2, Plus, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import PageHeader from "../PageHeader";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import ExerciseCard from "./ExerciseCard";
import ExercisePicker from "./ExercisePicker";
import FinishForm from "./FinishForm";
import LogSetSheet from "./LogSetSheet";
import PainDialog from "./PainDialog";
import RestTimerBar from "./RestTimerBar";
import SessionSummary from "./SessionSummary";
import { askEngine } from "./lib/askEngine";
import { dayKey, formatDay, formatTime, setLine, templateLabel } from "./lib/format";
import { planItemIn, prefillSet, quickSet } from "./lib/logSet";
import {
  MAX_ITEMS,
  addItem,
  addOptions,
  estimateMinutes,
  fromEngine,
  itemFor,
  moveAt,
  remainingSets,
  removeAt,
  setSetsAt,
  swapAt,
  swapOptions,
} from "./lib/planEdit";
import { restSeconds } from "./lib/restTimer";
import { createSetQueue } from "./lib/setQueue";
import {
  closedItselfAt,
  countByExercise,
  failureKind,
  jointPlace,
  mergeSets,
  painfulJointOf,
  painfulJoints,
  withoutSet,
} from "./lib/sessionSets";
import useRestTimer from "./lib/useRestTimer";
import useSetQueue, { sendLeftovers } from "./lib/useSetQueue";
import { warmupFor } from "./lib/warmup";

// What a plan PATCH answers that belongs to the plan (sets are the set
// requests' to change — see changePlan).
const PLAN_FIELDS = ["plan", "plan_version", "template", "minutes", "last_activity_at", "session_state", "finished_at"];
// What a re-read (GET) is believed about: whether it's still open, and the
// pain reported in it. Sets stay the set requests' to change.
const STATE_FIELDS = ["session_state", "finished_at", "last_activity_at", "pain_reports"];
const QUEUED_PREFIX = "queued:";

const setsWord = (n) => `${n} ${n === 1 ? "set" : "sets"}`;
const pick = (data, fields) =>
  Object.fromEntries(fields.filter((field) => field in (data ?? {})).map((field) => [field, data[field]]));

/**
 * The cards in order, with the exercise being brought in (W3) where it will
 * go: right after the slot it swaps, or after the plan's last card.
 */
const withGetting = (cards, getting, planned) => {
  if (!getting) return cards;
  const at = getting.at === null ? planned : getting.at + 1;
  return [...cards.slice(0, at), { key: getting.key, at: -2, waiting: getting }, ...cards.slice(at)];
};

/** An exercise the catalog doesn't have (archived, or another person's): enough to show and log it. */
const stubExercise = (key, name) => ({ key, name: name ?? key, load_kind: "external", requires: [], joints: [] });

/**
 * The session in progress (walkthroughs 4–7): exercise cards in plan order
 * (then any exercise logged off the plan), logging, the rest timer, pain,
 * plan changes, Finish, and the summary after it.
 *
 * Sets go through D14's write-ahead queue (lib/useSetQueue.js): each is on
 * this device the moment it's logged and on screen as "saving…" until mono
 * acknowledges it; a set that can't reach mono is sent again with the same
 * client_id — never twice. Plan changes PATCH the whole plan and take the
 * server's answer as the new plan; sets are only ever changed by the set
 * requests, so a plan answer can't drop a set logged meanwhile.
 *
 * Cards are plan SLOTS, addressed by index: a plan with the same exercise
 * twice (an agent's) changes the slot that was touched. Sets are logged and
 * shown in the session's units; a plan made in other units (a settings
 * switch after planning) has its loads converted for the cards and the
 * sheet, and keeps its own units when it's changed.
 *
 * D18: the view knows when its session has closed itself (idle 6 hours) —
 * on mount, when the page comes back, right before each log, and from a
 * re-read whenever a set's answer carries a notice. Closed, it offers no
 * way to log into it (sets queued while it was open still go): only a new
 * session, or feedback for this one.
 *
 * Contract with Today:
 * - Rendered inside Today's page container; this owns the page's h1.
 * - `session`: the TrainingSession as the API returned it. Its `notices`
 *   (D18's "Closed yesterday's session", walkthrough 10) are shown once —
 *   later copies (with no notices) don't clear them. Its `pain_reports`
 *   (with the check-in's joint pain) say which joints hurt today.
 * - `onSessionChange(session)`: every newer copy (a plan change, a set).
 * - `onFinished(notice?, {carry}?)`: the person is done with the session
 *   (finished and its summary read, ended, discarded, or closed by itself
 *   and a new one wanted) — Today goes back to the check-in, showing
 *   `notice` (an already-worded sentence, e.g. after a discard) until the
 *   next action; with `carry`, until the person leaves the next session
 *   (sets the closed one didn't take, to be logged there).
 *
 * Reads `me`, `settings` and `exercises` from the Training layout's outlet
 * context.
 */
function TrainingSession({ session, onFinished, onSessionChange }) {
  const { me, settings, exercises } = useOutletContext() || {};
  const userId = me?.id ?? null;
  const catalog = useMemo(() => exercises ?? [], [exercises]);
  const byKey = useMemo(() => new Map(catalog.map((e) => [e.key, e])), [catalog]);
  // Sets are stored in the session's units; the plan may be in its own.
  const units = session.units ?? settings?.units ?? "lb";
  const planUnits = session.plan?.units ?? units;
  const items = useMemo(() => session.plan?.items ?? [], [session.plan]);
  // What the cards and the sheet show: the plan's loads in the session's
  // units (edits send `items`, as planned).
  const shownItems = useMemo(
    () => items.map((item) => planItemIn(item, planUnits, units, byKey.get(item.exercise), settings)),
    [items, planUnits, units, byKey, settings],
  );

  // Render mirror: async answers build on the newest copy, not the one
  // their request started from.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const apply = useCallback(
    (next) => {
      sessionRef.current = next;
      onSessionChange(next);
    },
    [onSessionChange],
  );
  // Bumped by every local change to the sets: a plan answer requested
  // before one doesn't get to replace them.
  const setsGen = useRef(0);

  const [notices, setNotices] = useState(() => session.notices ?? []);
  const addNotices = useCallback((more) => {
    if (!more?.length) return;
    setNotices((current) => [...current, ...more.filter((notice) => !current.includes(notice))]);
  }, []);

  // --- D18: has it closed itself? ------------------------------------------
  // The clock as last read (on mount, when the page comes back, before a
  // log): an open copy idle 6 hours by it has closed itself.
  const [clock, setClock] = useState(() => Date.now());
  const closedAt = closedItselfAt(session, clock);
  const closed = closedAt !== null;
  const closedAlert = useRef(null);
  const reading = useRef(false);
  // Sets logged at the moment the clock found it closed ({key, body, at,
  // label}): not sent, and said in words with their numbers — a sheet that
  // closes on Log would otherwise take what was typed with it. If mono then
  // says it's still open, they're logged after all (below).
  const [notLogged, setNotLogged] = useState([]);

  /** Re-read the session and believe what it says about being open. */
  const refresh = useCallback(async () => {
    if (reading.current) return;
    reading.current = true;
    try {
      const { data } = await trainingApi.sessions.get(sessionRef.current.id);
      if (!mounted.current || !data) return;
      apply({ ...sessionRef.current, ...pick(data, STATE_FIELDS) });
      setClock(Date.now());
    } catch {
      // No answer: the clock's word stands until the next look.
    } finally {
      reading.current = false;
    }
  }, [apply]);

  useEffect(() => {
    const look = () => setClock(Date.now());
    document.addEventListener("visibilitychange", look);
    return () => document.removeEventListener("visibilitychange", look);
  }, []);

  // The clock says closed but this copy says open: ask mono (an agent's
  // set, say, may have kept it going) — its answer is believed.
  const clockSaysClosed = closed && (session.session_state ?? "open") === "open";
  useEffect(() => {
    if (clockSaysClosed) refresh();
  }, [clockSaysClosed, refresh]);

  // Closing takes the Log buttons away: focus that fell with them lands on
  // the explanation.
  useEffect(() => {
    if (!closedAt) return;
    const active = document.activeElement;
    if (!active || active === document.body) closedAlert.current?.focus();
  }, [closedAt]);

  // The client_ids the server has: a queued set among them isn't waiting.
  const serverIds = useMemo(() => new Set((session.sets ?? []).map((set) => set.client_id)), [session.sets]);
  const queue = useSetQueue({
    userId,
    sessionId: session.id,
    known: serverIds,
    send: (batch) => trainingApi.sets.log(session.id, batch),
    onSaved: (result) => {
      setsGen.current += 1;
      const latest = sessionRef.current;
      apply({ ...latest, sets: mergeSets(latest.sets, result.sets) });
      addNotices(result.notices);
      // A notice ("…it's now closed") can change what the session is.
      if (result.notices?.length) refresh();
    },
  });
  // A rest from another session (an old one, still in storage) isn't this
  // one's: not shown, and it doesn't tick, hold the screen or buzz here.
  const rest = useRestTimer(userId, { sessionId: session.id });
  const ownRest = rest.own;

  // Sets left queued by an earlier session (finished before they got
  // through) go now: the API takes sets into a finished session.
  useEffect(() => {
    let alive = true;
    sendLeftovers(userId, session.id, (id, batch) => trainingApi.sets.log(id, batch)).then(
      ({ sent, notices: told, dropped }) => {
        if (!alive) return;
        addNotices([
          ...(sent > 0 ? [`Saved ${setsWord(sent)} from an earlier session that hadn't reached mono yet.`] : []),
          ...told,
          ...(dropped.length
            ? [
                `${setsWord(dropped.length)} from an earlier session couldn't be saved: ${dropped
                  .map(({ text, reason }) => `${text} — ${reason}`)
                  .join("; ")}`,
              ]
            : []),
        ]);
      },
    );
    return () => {
      alive = false;
    };
  }, [userId, session.id, addNotices]);

  // --- pain today (walkthrough 6) -------------------------------------------
  const [reportedHere, setReportedHere] = useState([]);
  const painful = useMemo(() => painfulJoints(session, reportedHere), [session, reportedHere]);
  const painPlaces = [...painful].map(([joint, side]) => jointPlace(joint, side));

  const failure = queue.failure;
  // (A queued set whose client_id the server has is filtered here on the
  // render that shows it; useSetQueue's letGoKnown lets it go from storage
  // on the next. The filter has no failing test of its own: letGoKnown hides
  // the same set one render later — c9 S5.)
  const visibleQueued = queue.pending.filter((item) => !serverIds.has(item.client_id));
  // Refused sets weren't stored: not counted at the finish, never promised.
  const refusedQueued = visibleQueued.filter((item) => queue.refused[item.client_id]);
  const pendingQueued = visibleQueued.filter((item) => !queue.refused[item.client_id]);
  const logged = countByExercise(session.sets, visibleQueued);
  // Render mirror: a swap that waited for the engine counts the sets logged meanwhile.
  const loggedRef = useRef(logged);
  loggedRef.current = logged;
  const planKeys = useMemo(() => items.map((item) => item.exercise), [items]);
  const offPlan = [...new Set([...(session.sets ?? []), ...visibleQueued].map((set) => set.exercise))].filter(
    (key) => !planKeys.includes(key),
  );
  const exerciseOf = (key, name) => byKey.get(key) ?? stubExercise(key, name);
  const nameOf = (key, at = -1) => items[at]?.name ?? exerciseOf(key).name;
  const serverSetsOf = (key) => (session.sets ?? []).filter((set) => set.exercise === key);
  const queuedOf = (key) =>
    visibleQueued
      .filter((set) => set.exercise === key)
      .map((set) => ({
        ...set,
        id: `${QUEUED_PREFIX}${set.client_id}`,
        status: queue.refused[set.client_id] ? "refused" : failure ? "waiting" : "saving",
        error: queue.refused[set.client_id],
      }));
  const allSetsOf = (key) => [...serverSetsOf(key), ...queuedOf(key)];
  /** "Bench press 135 lb × 8 @ 2 in reserve": a set in words, for the queue and the finish. */
  const setText = (set) => set.label ?? `${nameOf(set.exercise)} ${setLine(set, units, exerciseOf(set.exercise))}`;

  // --- focus after a change: the element, once React has drawn it --------
  const focusSoon = (...selectors) => {
    setTimeout(() => {
      for (const selector of selectors) {
        const target = document.querySelector(selector);
        if (target && !target.disabled) {
          target.focus();
          return;
        }
      }
    }, 0);
  };
  const cardControl = (key, action) => `[data-card="${key}"] [data-action="${action}"]`;

  // --- plan changes -------------------------------------------------------
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState(null);
  const planSavingRef = useRef(false);

  /** PATCH the whole plan; resolves to {ok} or {ok: false, error}. */
  const changePlan = async (nextItems) => {
    if (planSavingRef.current) return { ok: false, error: "Another change is still saving." };
    if (nextItems === items) return { ok: true };
    planSavingRef.current = true;
    setPlanSaving(true);
    setPlanError(null);
    const current = sessionRef.current;
    const base = current.plan ?? { template: current.template || "custom", units: current.units };
    const gen = setsGen.current;
    try {
      const { data } = await trainingApi.sessions.patch(current.id, { plan: { ...base, items: nextItems } });
      if (!mounted.current) return { ok: true };
      const latest = sessionRef.current;
      // The answer's sets are newer than ours only if no set changed here
      // since the request went out.
      const fresh = gen === setsGen.current && Array.isArray(data?.sets);
      apply({ ...latest, ...pick(data, PLAN_FIELDS), ...(fresh ? { sets: data.sets } : {}) });
      return { ok: true };
    } catch (err) {
      const error = apiErrorMessage(err, "Couldn't change the plan — it's as it was.");
      if (mounted.current) setPlanError(error);
      return { ok: false, error };
    } finally {
      planSavingRef.current = false;
      if (mounted.current) setPlanSaving(false);
    }
  };
  // Render mirror: a change that waited for the engine compares against the
  // newest plan. (No test fails on the closed-over one: bringIn holds the
  // lock while it waits, so the plan can't change meanwhile — kept as the
  // house's "never call the closed-over function after an await".)
  const changePlanRef = useRef(changePlan);
  changePlanRef.current = changePlan;

  // An exercise brought in (W3): {key, name, at} while the engine answers
  // (at: the slot it swaps, null for an add); and, by exercise key, what the
  // engine said about doing it today, shown on its card.
  const [getting, setGetting] = useState(null);
  const [pickNotes, setPickNotes] = useState({});

  /**
   * Swap slot `at` for `exercise` (or add it, `at` null): ask the engine for
   * its numbers with the session's check-in (POST prescribe/), then PATCH
   * the plan once — a swap keeps the sets the slot has left, and the engine's
   * other numbers. Holds the one-change-at-a-time lock throughout, so nothing
   * else changes the plan meanwhile. If the engine can't answer it's
   * find-your-weight at 3 in reserve, saying so. Resolves like changePlan.
   */
  const bringIn = async (exercise, at = null) => {
    if (planSavingRef.current) return { ok: false, error: "Another change is still saving." };
    planSavingRef.current = true;
    setPlanSaving(true);
    setPlanError(null);
    setGetting({ key: exercise.key, name: exercise.name ?? exercise.key, at });
    const asked = sessionRef.current;
    let make;
    let notices = [];
    try {
      const got = await askEngine(exercise, {
        checkin: asked.checkin ?? null,
        units: asked.plan?.units ?? asked.units ?? units,
        settings,
      });
      notices = got.notices;
      make = (ex, { sets, slot } = {}) => fromEngine(got.item, ex, { slot, sets });
    } catch {
      make = (ex, options = {}) => itemFor(ex, { ...options, failed: true });
    } finally {
      planSavingRef.current = false;
    }
    if (!mounted.current) return { ok: false, error: null };
    // Handed straight on to the PATCH (same tick), which takes the lock again
    // — or, with nothing left to change, leaves it off.
    setPlanSaving(false);
    setGetting(null);
    setPickNotes((current) => ({ ...current, [exercise.key]: notices }));
    const latest = sessionRef.current.plan?.items ?? [];
    return changePlanRef.current(
      at === null ? addItem(latest, exercise, make(exercise)) : swapAt(latest, at, exercise, loggedRef.current, make),
    );
  };

  // --- logging --------------------------------------------------------------
  // {key, at, editing, open}: kept (closed) after use, so the sheet can close
  // properly — its focus return runs as it unmounts its content.
  const [sheet, setSheet] = useState(null);
  const sheetOpener = useRef(null);

  /** Log a set of the card at plan slot `at` (−1 off the plan) — unless the session has closed itself. */
  const logSet = (key, body, at = -1) => {
    const now = Date.now();
    const label = `${nameOf(key, at)} ${setLine(body, units, exerciseOf(key))}`;
    if (closedItselfAt(sessionRef.current, now)) {
      setClock(now); // says so, and takes the Log buttons away
      setNotLogged((current) => [...current, { key, body, at, label }]);
      return;
    }
    queue.log(body, { label });
    rest.start({
      seconds: restSeconds(items[at] ?? null, byKey.get(key)),
      sessionId: session.id,
      exercise: key,
    });
  };
  // Held back by the clock, but mono's answer says the session is still open
  // (an agent or another device kept it going — refresh): logged after all,
  // as typed, and the rest starts for the last of them. Closed — or finished
  // since, its feedback added — they stay listed, and go with the person
  // when they leave (`leave`).
  const { log: queueLog } = queue;
  const { start: startRest } = rest;
  const stillOpen = !closed && (session.session_state ?? "open") === "open" && !session.finished_at;
  useEffect(() => {
    if (!stillOpen || notLogged.length === 0) return;
    for (const held of notLogged) queueLog(held.body, { label: held.label });
    const last = notLogged.at(-1);
    startRest({
      seconds: restSeconds(items[last.at] ?? null, byKey.get(last.key)),
      sessionId: session.id,
      exercise: last.key,
    });
    setNotLogged([]);
  }, [stillOpen, notLogged, queueLog, startRest, items, byKey, session.id]);
  /** The sets the closed session didn't take, in words: "<lead>: Bench press 140 lb × 4 … — log it in the new session." */
  const notLoggedLine = (lead) =>
    `${lead}: ${notLogged.map((held) => held.label).join("; ")} — log ${notLogged.length === 1 ? "it" : "them"} in the new session.`;

  const saveSet = async (setId, body) => {
    if (String(setId).startsWith(QUEUED_PREFIX)) {
      queue.replace(String(setId).slice(QUEUED_PREFIX.length), body, {
        label: `${nameOf(body.exercise)} ${setLine(body, units, exerciseOf(body.exercise))}`,
      });
      return;
    }
    const { data } = await trainingApi.sets.patch(session.id, setId, body);
    if (!mounted.current) return;
    setsGen.current += 1;
    const latest = sessionRef.current;
    apply({ ...latest, sets: mergeSets(latest.sets, [data]) });
  };

  const deleteSet = async (setId) => {
    if (String(setId).startsWith(QUEUED_PREFIX)) {
      queue.discard(String(setId).slice(QUEUED_PREFIX.length));
      return;
    }
    try {
      await trainingApi.sets.remove(session.id, setId);
    } catch (err) {
      if (failureKind(err) !== "gone") throw err; // already gone: as asked
    }
    if (!mounted.current) return;
    setsGen.current += 1;
    const latest = sessionRef.current;
    apply({ ...latest, sets: withoutSet(latest.sets, setId) });
  };

  const openSheet = (key, at, editing = null) => {
    sheetOpener.current = document.activeElement;
    setSheet({ key, at, editing, open: true });
  };

  // --- swap / add (the exercise picker) and pain ---------------------------
  const [picker, setPicker] = useState(null); // {mode: "swap", key, at} | {mode: "add"}
  const pickedKey = useRef(null);
  // The card whose ⋯ menu (or warning) opened a swap: a cancel goes back
  // to it (the menu item that opened the picker is gone with its menu).
  const swapFrom = useRef(null);
  const [pain, setPain] = useState(null); // {key, at, open}, kept like `sheet`
  const painFocus = useRef(null);

  const swapping = picker?.mode === "swap" ? (items[picker.at] ?? null) : null;
  const choices = useMemo(() => {
    if (!picker) return { options: [], unavailable: [] };
    if (picker.mode === "add") return addOptions(catalog, settings, { exclude: planKeys });
    const found = swapOptions(byKey.get(picker.key), catalog, settings, { exclude: planKeys });
    // Never a swap into a joint that hurts today.
    const spares = (option) => !(option.joints ?? []).some((joint) => painful.has(joint));
    return { ...found, options: found.options.filter(spares) };
  }, [picker, catalog, settings, planKeys, byKey, painful]);

  const swapDescription = () => {
    if (!swapping) return "";
    const name = swapping.name ?? exerciseOf(swapping.exercise).name;
    const done = logged[swapping.exercise] ?? 0;
    const left = remainingSets(swapping, logged);
    const spared = painPlaces.length ? ` Exercises that load ${painPlaces.join(" or ")} are left out.` : "";
    if (done > 0) {
      return `Same muscles, with your equipment, best first. The new exercise takes the ${setsWord(left)} still to do; the ${done} logged stay with ${name}. Its reps and weight come from your history.${spared}`;
    }
    return `Same muscles, with your equipment, best first. The new exercise takes its ${setsWord(left)}; its reps and weight come from your history.${spared}`;
  };

  const onPick = async (exercise) => {
    const target = picker;
    pickedKey.current = exercise.key;
    setPicker(null);
    const swap = target?.mode === "swap";
    const { ok } = await bringIn(exercise, swap ? target.at : null);
    if (ok) focusSoon(cardControl(exercise.key, "quick"), cardControl(exercise.key, "log"));
    else focusSoon(...(swap ? [cardControl(target.key, "menu")] : []), "[data-action=add-exercise]");
  };

  /** Drop the sets left at slot `at`; focus goes where the change left off. */
  const skipAt = async (key, at) => {
    const next = items[at + 1]?.exercise;
    const { ok } = await changePlan(removeAt(items, at, logged));
    if (ok) {
      focusSoon(cardControl(key, "pain"), ...(next ? [cardControl(next, "pain")] : []), "[data-action=add-exercise]");
    }
  };

  // --- finish --------------------------------------------------------------
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(null);
  const finishHeading = useRef(null);
  const asSets = (queued) => queued.map((set) => ({ ...set, id: `${QUEUED_PREFIX}${set.client_id}` }));
  const withQueued = (value) => ({ ...value, sets: [...(value.sets ?? []), ...asSets(pendingQueued)] });

  const finishedWith = (data) => {
    rest.skip();
    setFinished(data);
    apply({ ...sessionRef.current, ...data, sets: mergeSets(sessionRef.current.sets, data.sets) });
    setTimeout(() => finishHeading.current?.focus(), 0);
  };
  /**
   * Mono has discarded it: the sets this device still had queued for it go
   * too — sent later they'd 404, and the next Today load would report them
   * as "couldn't be saved". The form calls this even when the person left
   * Today while the discard ran (this view is gone then), so it touches
   * storage only, for that session alone; `discarded` below does the screen.
   */
  const forgetQueued = (sessionId) => {
    if (userId !== null) createSetQueue(userId, sessionId).clear();
  };
  // Asked for, before any answer: a reload or a closed tab can lose it, and
  // the next Today load then finds the session gone — the mark tells it the
  // sets went with it, as asked, not "couldn't be saved". Mono's no unmarks.
  const markDiscarding = (sessionId) => {
    if (userId !== null) createSetQueue(userId, sessionId).markDiscarding();
  };
  const unmarkDiscarding = (sessionId) => {
    if (userId !== null) createSetQueue(userId, sessionId).unmarkDiscarding();
  };
  const discarded = () => {
    // The count the confirm said ("Its 3 logged sets go with it").
    const count = withQueued(sessionRef.current).sets.length;
    // The queue on screen (storage went in forgetQueued; with no signed-in
    // id it was only ever here).
    queue.clear();
    rest.skip();
    // One that closed itself on an earlier day is named by its day, as
    // History names it ("the Upper session of Tue, Oct 6").
    const { template, started_at: startedAt } = sessionRef.current;
    const zone = settings?.timezone;
    const day = formatDay(startedAt, zone);
    const which =
      !day || dayKey(startedAt, zone) === dayKey(new Date(), zone)
        ? `today's ${templateLabel(template)} session`
        : `the ${templateLabel(template)} session of ${day}`;
    onFinished(`Discarded ${which}${count ? ` and its ${setsWord(count)}` : ""}.`);
  };
  // Sets the closed session didn't take go with the person, numbers and
  // all: Today keeps saying them through the check-in and into the new
  // session, where they're logged (`carry`).
  const leave = () =>
    notLogged.length ? onFinished(notLoggedLine("Not logged in the closed session"), { carry: true }) : onFinished();
  const startNew = () => {
    rest.skip();
    leave();
  };
  const backToToday = () => {
    // Refused sets were listed as not saved: said once, then put away (so
    // Today doesn't report them again).
    for (const set of refusedQueued) queue.discard(set.client_id);
    leave();
  };

  // --- render ---------------------------------------------------------------
  const unsaved = visibleQueued.length;
  const queueLine = () => {
    const refusedCount = refusedQueued.length;
    if (!unsaved) return Object.keys(logged).length ? "Every set is saved." : "";
    if (failure?.kind === "gone") {
      return `This session isn't on mono any more (discarded somewhere else?), so ${setsWord(unsaved)} couldn't be saved.`;
    }
    if (failure?.kind === "signed-out") {
      return `You've been signed out. ${setsWord(unsaved)} wait on this device — sign in again and they're sent.`;
    }
    if (refusedCount === unsaved) return `${setsWord(unsaved)} not saved — see below to fix or discard.`;
    if (failure?.kind === "throttled") return `${setsWord(unsaved)} waiting — mono asked to slow down; sending again shortly.`;
    if (failure) {
      return `${setsWord(unsaved)} not saved yet — no answer from mono. They're kept on this device and sent again on their own.`;
    }
    return `Saving ${setsWord(unsaved)}…`;
  };
  const line = queueLine();
  const minutes = estimateMinutes(items, session.template);
  const started = formatTime(session.started_at, settings?.timezone);
  // The plan's warm-up line (lib/warmup, as PlanView shows it — a lift
  // still finding its weight gets its kit's generic ramp, W13), until the
  // lift has started.
  const firstWarmup = (() => {
    const warmup = warmupFor(shownItems, byKey, settings, units);
    return warmup && !((logged[warmup.key] ?? 0) > 0) ? { key: warmup.key, text: warmup.line } : null;
  })();

  // Plan slots in order, then exercises logged off the plan — and, while the
  // engine answers, the exercise being brought in where it will go. A card's
  // React key is its exercise and which time it's in the plan (`#n`): unique
  // with the same exercise twice (an agent's plan), and a card's own state
  // (an open Why) moves with it when the plan is reordered or shortened.
  const seen = new Map();
  const cards = withGetting(
    [
      ...items.map((item, at) => {
        const nth = seen.get(item.exercise) ?? 0;
        seen.set(item.exercise, nth + 1);
        return { key: item.exercise, at, id: `plan-${item.exercise}#${nth}` };
      }),
      ...offPlan.map((key) => ({ key, at: -1, id: `off-${key}` })),
    ],
    getting,
    items.length,
  );

  const sheetKey = sheet?.key ?? null;
  const sheetItem = sheetKey && sheet.at >= 0 ? (shownItems[sheet.at] ?? null) : null;
  const sheetExercise = sheetKey ? exerciseOf(sheetKey, sheetItem?.name) : null;
  const painKey = pain?.key ?? null;
  const painItem = painKey && pain.at >= 0 ? (shownItems[pain.at] ?? null) : null;
  const painExercise = painKey ? exerciseOf(painKey, painItem?.name) : null;

  if (finished) {
    const pendingCount = pendingQueued.length;
    const refusedCount = refusedQueued.length;
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Today" subtitle={`${templateLabel(finished.template)} session finished.`} />
        <div ref={finishHeading} tabIndex={-1} className="outline-none">
          {/* The live sets: one still queued at the finish is saved since. */}
          <SessionSummary session={withQueued({ ...finished, sets: session.sets })} exercises={catalog} />
        </div>
        {refusedCount > 0 && (
          <div role="status" className="flex flex-col gap-1 text-sm text-foreground/80">
            <p className="m-0">
              {setsWord(refusedCount)} {refusedCount === 1 ? "wasn't" : "weren't"} saved — mono refused{" "}
              {refusedCount === 1 ? "it" : "them"}:
            </p>
            <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
              {refusedQueued.map((set) => (
                <li key={set.client_id}>{`${setText(set)} — ${queue.refused[set.client_id]}`}</li>
              ))}
            </ul>
          </div>
        )}
        {notLogged.length > 0 && (
          <p role="status" className="m-0 text-sm font-medium">
            {notLoggedLine("Not logged, as the session had closed")}
          </p>
        )}
        {pendingCount > 0 && (
          <p role="status" className="m-0 text-sm text-foreground/80">
            {setsWord(pendingCount)} still {pendingCount === 1 ? "isn't" : "aren't"} saved. Stay on this page until{" "}
            {pendingCount === 1 ? "it is" : "they are"} — or {pendingCount === 1 ? "it's" : "they're"} kept on
            this device and sent the next time you open a session here.
          </p>
        )}
        <div>
          <Button type="button" className="h-11 w-full md:h-10 md:w-auto" onClick={backToToday}>
            Back to Today
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Today"
        subtitle={`${templateLabel(session.template)} session${started ? `, started ${started}` : ""}${items.length ? ` · ~${Math.round(minutes)} min planned` : ""}.`}
      />

      {closed && (
        <Alert ref={closedAlert} role="status" tabIndex={-1} data-closed-alert="" className="outline-none">
          <AlertDescription className="flex flex-col gap-3 text-foreground">
            <p className="m-0">
              This session closed itself at {formatTime(closedAt, settings?.timezone)} — it had been idle for over 6
              hours. New sets go in a new session (today&apos;s check-in comes first); this one can still take its
              feedback.
            </p>
            {notLogged.length > 0 && (
              <p className="m-0 font-medium">{notLoggedLine("Not logged, as the session had closed")}</p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button type="button" className="h-11 md:h-9" onClick={startNew}>
                Start a new session
              </Button>
              {!finishing && (
                <Button type="button" variant="outline" className="h-11 md:h-9" onClick={() => setFinishing(true)}>
                  Add feedback to it
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {notices.length > 0 && (
        <Alert role="status">
          <AlertDescription className="flex items-start gap-2 text-foreground">
            <ul className="m-0 flex flex-1 list-disc flex-col gap-1 pl-4">
              {notices.map((notice) => (
                <li key={notice}>{notice}</li>
              ))}
            </ul>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-my-2 size-11 shrink-0 md:size-8"
              aria-label="Dismiss these notes"
              title="Dismiss these notes"
              onClick={() => setNotices([])}
            >
              <X aria-hidden="true" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {(line || !queue.persisted) && (
        <div className="flex flex-wrap items-center gap-2">
          <p role="status" className="m-0 flex-1 text-sm text-foreground/80">
            {line}
            {!queue.persisted && unsaved > 0
              ? " This browser isn't keeping unsaved sets (its storage is blocked) — keep this page open until they're saved."
              : ""}
          </p>
          {failure && failure.kind !== "gone" && failure.kind !== "signed-out" && (
            <Button type="button" variant="outline" className="h-11 md:h-8" onClick={queue.retry}>
              Send now
            </Button>
          )}
        </div>
      )}

      {planSaving && (
        <p role="status" className="m-0 flex items-center gap-2 text-sm text-foreground/80">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          {getting ? `Getting ${getting.name}'s numbers…` : "Saving the plan…"}
        </p>
      )}
      {planError && (
        <Alert variant="destructive">
          <AlertDescription>{planError}</AlertDescription>
        </Alert>
      )}

      {items.length === 0 && offPlan.length === 0 && !getting ? (
        <p className="m-0 rounded-lg border border-dashed bg-card p-4 text-sm text-muted-foreground">
          Nothing planned yet — add the first exercise below. Each gets its sets, reps and weight from your
          history.
        </p>
      ) : (
        <ol aria-label="This session's exercises" className="m-0 flex list-none flex-col gap-3 p-0">
          {cards.map(
            ({ key, at, id, waiting }) => {
              if (waiting) {
                // The exercise brought in, until the engine's numbers are in (W3).
                return (
                  <li key={`getting-${key}`} className="rounded-lg border border-dashed bg-card p-4">
                    <h2 className="m-0 font-display text-lg font-semibold">{waiting.name}</h2>
                    <p className="mt-1 mb-0 flex items-center gap-2 text-sm text-foreground/80">
                      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                      Getting its numbers…
                    </p>
                  </li>
                );
              }
              const item = at >= 0 ? shownItems[at] : null;
              const exercise = byKey.get(key);
              const shown = exerciseOf(key, item?.name);
              const name = item?.name ?? shown.name;
              const all = allSetsOf(key);
              const quick = quickSet(prefillSet({ item, exercise: shown, sets: all, settings, units }), shown);
              const hurts = item && remainingSets(item, logged) > 0 ? painfulJointOf(shown, painful) : null;
              return (
                <ExerciseCard
                  key={id}
                  cardKey={key}
                  name={name}
                  item={item}
                  exercise={exercise}
                  units={units}
                  sets={serverSetsOf(key)}
                  queued={queuedOf(key)}
                  quick={quick}
                  warmup={firstWarmup?.key === key ? firstWarmup.text : null}
                  notices={pickNotes[key] ?? []}
                  index={at}
                  count={items.length}
                  logged={logged[key] ?? 0}
                  // a closed session's plan isn't changed any more (its ⋯
                  // menu and a warning's Swap / Skip)
                  disabled={planSaving || closed}
                  canLog={!closed}
                  reasonUnits={item && planUnits !== units ? planUnits : null}
                  warning={
                    hurts
                      ? `Loads ${jointPlace(hurts.joint, hurts.side)}, which you reported today. Swap it or skip it — don't push through pain.`
                      : null
                  }
                  onQuickLog={(body) => logSet(key, body, at)}
                  onOpenLog={() => openSheet(key, at)}
                  onEditSet={(set) => openSheet(key, at, set)}
                  onFixQueued={(clientId) => {
                    const queued = queuedOf(key).find((set) => set.client_id === clientId);
                    if (queued) openSheet(key, at, { ...queued, can_delete: true });
                  }}
                  onDiscardQueued={(clientId) => queue.discard(clientId)}
                  onPain={() => setPain({ key, at, open: true })}
                  onSwap={() => {
                    swapFrom.current = key;
                    setPicker({ mode: "swap", key, at });
                  }}
                  onSkipToday={() => skipAt(key, at)}
                  onChangeSets={(delta) => changePlan(setSetsAt(items, at, (items[at]?.sets ?? 0) + delta, logged))}
                  onMove={async (direction) => {
                    const { ok } = await changePlan(moveAt(items, at, direction));
                    if (ok) focusSoon(cardControl(key, "menu"));
                  }}
                  onRemove={async () => {
                    const next = items[at + 1]?.exercise ?? items[at - 1]?.exercise;
                    const { ok } = await changePlan(removeAt(items, at, logged));
                    if (ok) {
                      focusSoon(
                        cardControl(key, "menu"),
                        ...(next ? [cardControl(next, "menu")] : []),
                        "[data-action=add-exercise]",
                      );
                    }
                  }}
                />
              );
            },
          )}
        </ol>
      )}

      {!closed && (
        <div className="flex flex-col gap-1">
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full md:h-9 md:w-auto md:self-start"
            data-action="add-exercise"
            disabled={planSaving || items.length >= MAX_ITEMS}
            onClick={() => setPicker({ mode: "add" })}
          >
            <Plus aria-hidden="true" />
            Add an exercise
          </Button>
          <p className="m-0 text-xs text-foreground/80">
            {items.length >= MAX_ITEMS
              ? `A plan holds up to ${MAX_ITEMS} exercises — remove one to add another.`
              : "It's added at the end; its sets, reps and weight come from your history, as the plan's do."}
          </p>
        </div>
      )}

      {finishing ? (
        <div className="flex flex-col gap-2">
          {/* The way back lives inside the form, which disables it while a
              finish or discard is in flight (leaving mid-request would drop
              its answer: the server finished, the page still says open). */}
          <FinishForm
            session={withQueued(session)}
            exercises={catalog}
            onFinished={finishedWith}
            onDiscarded={discarded}
            onDiscardConfirmed={forgetQueued}
            onDiscardAsked={markDiscarding}
            onDiscardRefused={unmarkDiscarding}
            onCancel={() => {
              setFinishing(false);
              // The form (and the focus in it) is gone: back to what opened it.
              focusSoon("[data-action=finish]");
            }}
            cancelLabel="Not finished — back to the session"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1 border-t pt-4">
          <Button
            type="button"
            className="h-11 w-full md:h-10 md:w-auto md:self-start"
            data-action="finish"
            onClick={() => setFinishing(true)}
          >
            <Flag aria-hidden="true" />
            Finish…
          </Button>
          <p className="m-0 text-xs text-foreground/80">
            Opens a short form: how each trained muscle felt (optional). Or end without feedback, or
            discard the session.
          </p>
        </div>
      )}

      {/* Room for the pinned rest timer, so it never hides the last card. */}
      {ownRest && <div aria-hidden="true" className="h-36" />}

      <RestTimerBar
        rest={ownRest ? rest.rest : null}
        remaining={rest.remaining}
        total={rest.total}
        running={ownRest && rest.running}
        ended={ownRest && rest.ended}
        label={ownRest && rest.rest.exercise ? nameOf(rest.rest.exercise, planKeys.indexOf(rest.rest.exercise)) : null}
        wakeLockSupported={rest.wakeLockSupported}
        onExtend={() => rest.extend()}
        onSkip={rest.skip}
      />

      {sheetExercise && (
        <LogSetSheet
          open={sheet.open}
          onOpenChange={(open) => setSheet((current) => (current ? { ...current, open } : current))}
          exercise={sheetExercise}
          item={sheetItem}
          sets={allSetsOf(sheetKey)}
          settings={settings}
          units={units}
          editing={sheet?.editing ?? null}
          onLog={(body) => logSet(sheetKey, body, sheet.at)}
          onSave={saveSet}
          onDelete={deleteSet}
          onCloseAutoFocus={(event) => {
            // The opener can be gone (a quick-log button whose set is done,
            // or every Log once the session closed itself): land on the
            // card's own Log button — or the explanation — not <body>.
            const opener = sheetOpener.current;
            if (opener && document.contains(opener) && !opener.disabled) return;
            event.preventDefault();
            focusSoon(cardControl(sheetKey, "quick"), cardControl(sheetKey, "log"), "[data-closed-alert]");
          }}
        />
      )}

      <ExercisePicker
        open={picker !== null}
        onOpenChange={(open) => {
          if (!open) setPicker(null);
        }}
        title={swapping ? `Swap ${swapping.name ?? exerciseOf(swapping.exercise).name}` : "Add an exercise"}
        description={
          swapping
            ? swapDescription()
            : "Anything your equipment allows. It's added at the end; its sets, reps and weight come from your history, as the plan's do."
        }
        options={choices.options}
        unavailable={choices.unavailable}
        settings={settings}
        muscleFilter={!swapping}
        onPick={onPick}
        onCloseAutoFocus={(event) => {
          // A pick focuses the new card once it's saved (onPick); a cancel
          // from a card's menu goes back to that menu's button.
          const from = swapFrom.current;
          swapFrom.current = null;
          if (pickedKey.current) {
            pickedKey.current = null;
            event.preventDefault();
          } else if (from) {
            event.preventDefault();
            focusSoon(cardControl(from, "menu"), cardControl(from, "warning-swap"));
          }
        }}
      />

      {painExercise && (
        <PainDialog
          open={pain.open}
          onOpenChange={(open) => setPain((current) => (current ? { ...current, open } : current))}
          exercise={painExercise}
          item={painItem}
          sessionId={session.id}
          exercises={catalog}
          settings={settings}
          exclude={planKeys}
          remaining={painItem ? remainingSets(painItem, logged) : 0}
          painful={painful}
          closed={closed}
          onReported={(report) => setReportedHere((current) => [...current, report])}
          onSwap={(exercise) => {
            painFocus.current = [cardControl(exercise.key, "quick"), cardControl(exercise.key, "log")];
            return bringIn(exercise, pain.at);
          }}
          onSkip={() => {
            const next = items[pain.at + 1]?.exercise;
            painFocus.current = [
              cardControl(painKey, "pain"),
              ...(next ? [cardControl(next, "pain")] : []),
              "[data-action=add-exercise]",
            ];
            return changePlan(removeAt(items, pain.at, logged));
          }}
          onCloseAutoFocus={(event) => {
            // After a swap or a skip the Pain button that opened it may be
            // gone with its card: go where the change left off.
            const targets = painFocus.current;
            painFocus.current = null;
            if (!targets) return;
            event.preventDefault();
            focusSoon(...targets);
          }}
        />
      )}
    </div>
  );
}

TrainingSession.propTypes = {
  /** The open (or just finished) TrainingSession. */
  session: PropTypes.shape({
    id: PropTypes.number.isRequired,
    template: PropTypes.string,
    units: PropTypes.string,
    started_at: PropTypes.string,
    last_activity_at: PropTypes.string,
    finished_at: PropTypes.string,
    session_state: PropTypes.string,
    checkin: PropTypes.object,
    plan: PropTypes.shape({
      units: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.object),
    }),
    sets: PropTypes.arrayOf(PropTypes.object),
    notices: PropTypes.arrayOf(PropTypes.string),
    pain_reports: PropTypes.arrayOf(PropTypes.object),
    can_discard: PropTypes.bool,
  }).isRequired,
  /** The person is done with it: back to Today's check-in, with an optional worded notice. */
  onFinished: PropTypes.func.isRequired,
  /** A newer copy of the session (after any change). */
  onSessionChange: PropTypes.func.isRequired,
};

export default TrainingSession;
